import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ConfigProvider, theme as antdTheme } from "antd";

const THEME_STORAGE_KEY = "isDarkMod";

interface ThemeContextType {
  isDarkMode: boolean;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const getStoredTheme = () => {
  if (typeof window === "undefined") return false;

  const storedDarkMode = localStorage.getItem(THEME_STORAGE_KEY);
  if (storedDarkMode !== null) {
    return storedDarkMode === "true";
  }

  return localStorage.getItem("theme") === "dark";
};

const getAntdThemeTokens = (isDarkMode: boolean) => ({
  colorPrimary: "#6366f1",
  borderRadius: 8,
  fontFamily:
    "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  ...(isDarkMode
    ? {
        colorBgBase: "#111521",
        colorBgLayout: "#10131d",
        colorBgContainer: "#1a1f2d",
        colorBgElevated: "#181d2a",
        colorFillSecondary: "rgba(148, 163, 184, 0.12)",
        colorFillTertiary: "rgba(148, 163, 184, 0.08)",
        colorBorder: "#343b4d",
        colorBorderSecondary: "#2b3142",
        colorText: "#e9edf5",
        colorTextSecondary: "#b6bfcc",
        colorTextTertiary: "#8993a3",
        colorTextPlaceholder: "#7d8797",
        controlItemBgHover: "rgba(129, 140, 248, 0.12)",
      }
    : {
        colorBgBase: "#ffffff",
        colorBgLayout: "#f9fafb",
        colorBgContainer: "#ffffff",
        colorBgElevated: "#ffffff",
        colorBorder: "#e5e7eb",
        colorText: "#111827",
        colorTextSecondary: "#4b5563",
      }),
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(getStoredTheme);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.toggle("dark", isDarkMode);
    localStorage.setItem(THEME_STORAGE_KEY, String(isDarkMode));
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  const value = useMemo(
    () => ({
      isDarkMode,
      setIsDarkMode,
      toggleTheme: () => setIsDarkMode((current) => !current),
    }),
    [isDarkMode],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: isDarkMode
            ? antdTheme.darkAlgorithm
            : antdTheme.defaultAlgorithm,
          token: getAntdThemeTokens(isDarkMode),
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider.");
  }

  return context;
};
